import { fetchApi } from "../../config/fetchConfig";
import { sanitizeErrorForLogging } from "../../utils/utils";

const TRANSLATION_BODY = `
    id
    project_id
    import_profile_id
    name
    column_name
    operator
    find_value
    replace_value
    state
    source
    last_run_at
    last_run_matched_rows
    last_run_changed_rows
    last_run_outcome
    created_at
    updated_at
`;

const UI_TO_GRAPHQL_OPERATOR = {
    eq: "EQ",
    neq: "NEQ",
    contains: "CONTAINS",
    starts_with: "STARTS_WITH",
    ends_with: "ENDS_WITH",
    is_null: "IS_NULL",
    is_not_null: "IS_NOT_NULL",
    in: "IN",
};

const GRAPHQL_TO_UI_OPERATOR = Object.fromEntries(
    Object.entries(UI_TO_GRAPHQL_OPERATOR).map(([ui, gql]) => [gql, ui]),
);

const toGraphQLOperator = (operator) => UI_TO_GRAPHQL_OPERATOR[operator] || operator;

const normalizeTranslation = (translation) => {
    if (!translation) return translation;
    return {
        ...translation,
        operator: GRAPHQL_TO_UI_OPERATOR[translation.operator] || translation.operator,
    };
};

export const listStagingTranslationsAction = (profileId, projectId) => (dispatch, getState) =>
    fetchApi(
        "/graphql",
        {
            query: `query($profileId:Int!,$projectId:String!){
                list_staging_translations(profileId:$profileId,projectId:$projectId){
                    items {${TRANSLATION_BODY}}
                }
            }`,
            variables: { profileId: Number(profileId), projectId: String(projectId) },
        },
        {},
        { dispatch, user: getState()?.user },
    )
        .then((res) => res.json().catch(() => ({})))
        .then((res) => {
            const items = res?.data?.list_staging_translations?.items;
            if (res?.errors?.length || !Array.isArray(items)) return null;
            return items.map(normalizeTranslation);
        })
        .catch((err) => { console.error(JSON.stringify(sanitizeErrorForLogging(err))); return null; }); // eslint-disable-line no-console

export const createStagingTranslationAction =
    ({ projectId, importProfileId, name, columnName, operator, findValue, replaceValue }) =>
        (dispatch, getState) =>
            fetchApi(
                "/graphql",
                {
                    query: `mutation($input:CreateStagingTranslationInput!){
                        create_staging_translation(input:$input){${TRANSLATION_BODY}}
                    }`,
                    variables: {
                        input: {
                            projectId: String(projectId),
                            importProfileId: Number(importProfileId),
                            name,
                            columnName,
                            operator: toGraphQLOperator(operator),
                            findValue: findValue ?? null,
                            replaceValue,
                        },
                    },
                },
                {},
                { dispatch, user: getState()?.user },
            )
                .then((res) => res.json().catch(() => ({})))
                .then((res) => normalizeTranslation(res?.data?.create_staging_translation || null))
                .catch((err) => { console.error(JSON.stringify(sanitizeErrorForLogging(err))); return null; }); // eslint-disable-line no-console

export const updateStagingTranslationAction =
    (translationId, { name, columnName, operator, findValue, replaceValue }) =>
        (dispatch, getState) =>
            fetchApi(
                "/graphql",
                {
                    query: `mutation($translationId:Int!,$input:UpdateStagingTranslationInput!){
                        update_staging_translation(translationId:$translationId,input:$input){${TRANSLATION_BODY}}
                    }`,
                    variables: {
                        translationId: Number(translationId),
                        input: {
                            name: name ?? null,
                            columnName: columnName ?? null,
                            operator: operator ? toGraphQLOperator(operator) : null,
                            findValue: findValue ?? null,
                            replaceValue: replaceValue ?? null,
                        },
                    },
                },
                {},
                { dispatch, user: getState()?.user },
            )
                .then((res) => res.json().catch(() => ({})))
                .then((res) => normalizeTranslation(res?.data?.update_staging_translation || null))
                .catch((err) => { console.error(JSON.stringify(sanitizeErrorForLogging(err))); return null; }); // eslint-disable-line no-console

export const toggleStagingTranslationStateAction =
    (translationId, state) =>
        (dispatch, getState) =>
            fetchApi(
                "/graphql",
                {
                    query: `mutation($translationId:Int!,$state:StagingTranslationToggleState!){
                        toggle_staging_translation_state(translationId:$translationId,state:$state){${TRANSLATION_BODY}}
                    }`,
                    variables: { translationId: Number(translationId), state },
                },
                {},
                { dispatch, user: getState()?.user },
            )
                .then((res) => res.json().catch(() => ({})))
                .then((res) => normalizeTranslation(res?.data?.toggle_staging_translation_state || null))
                .catch((err) => { console.error(JSON.stringify(sanitizeErrorForLogging(err))); return null; }); // eslint-disable-line no-console

export const softDeleteStagingTranslationAction =
    (translationId) =>
        (dispatch, getState) =>
            fetchApi(
                "/graphql",
                {
                    query: `mutation($translationId:Int!){
                        soft_delete_staging_translation(translationId:$translationId){ success message }
                    }`,
                    variables: { translationId: Number(translationId) },
                },
                {},
                { dispatch, user: getState()?.user },
            )
                .then((res) => res.json().catch(() => ({})))
                .then((res) => res?.data?.soft_delete_staging_translation || { success: false })
                .catch((err) => { console.error(JSON.stringify(sanitizeErrorForLogging(err))); return { success: false }; }); // eslint-disable-line no-console

export const applyStagingTranslationAction =
    (translationId, profileId, projectId) =>
        (dispatch, getState) =>
            fetchApi(
                "/graphql",
                {
                    query: `mutation($translationId:Int!,$profileId:Int!,$projectId:String!){
                        apply_staging_translation(translationId:$translationId,profileId:$profileId,projectId:$projectId){
                            message queued
                        }
                    }`,
                    variables: {
                        translationId: Number(translationId),
                        profileId: Number(profileId),
                        projectId: String(projectId),
                    },
                },
                {},
                { dispatch, user: getState()?.user },
            )
                .then((res) => res.json().catch(() => ({})))
                .then((res) => res?.data?.apply_staging_translation || null)
                .catch((err) => { console.error(JSON.stringify(sanitizeErrorForLogging(err))); return null; }); // eslint-disable-line no-console
